/**
 * Catching an email address that can never receive mail, at the point someone types it.
 *
 * A customer signed up as `tgasupplies@gmail` — no TLD. Neither the browser's native `type="email"`
 * validation nor Cognito rejects that: both are satisfied by any `something@something`. So the account was
 * created, Cognito sent a verification code, and the domain had no MX or A record to resolve. The receiving
 * side answers `421 4.4.1`, which is a TRANSIENT code, so SES is obliged to keep retrying a message that
 * could never be delivered — fourteen hours of retries, each one raising a DeliveryDelay alarm, for an
 * address that was doomed from the first attempt.
 *
 * The customer noticed and re-registered correctly half an hour later. The cost was entirely ours.
 *
 * Deliberately NOT a full RFC 5322 parser, and deliberately not a TLD allowlist — either would eventually
 * reject a real customer's real address, which is a far worse failure than the one being prevented. This
 * only rejects addresses that are structurally incapable of resolving.
 */

/** The longest real TLD is 24 characters; anything longer is a typo, not a domain. */
const MAX_TLD = 24;

/**
 * Why this address cannot receive mail, or null when it looks deliverable.
 *
 * The message is shown to the person typing, so it says what to fix rather than that something is invalid.
 */
export function emailDomainProblem(raw: string): string | null {
  const email = raw.trim();
  if (!email) return null; // Emptiness is the required-field check's business, not ours.

  // lastIndexOf: the local part may legitimately contain '@' when quoted, the domain never can.
  const at = email.lastIndexOf('@');
  if (at < 1) return 'Enter a complete email address, including the @ and the domain.';

  const domain = email.slice(at + 1).toLowerCase();
  if (!domain) return 'Enter the part after the @ — for example, gmail.com.';
  if (/\s/.test(domain)) return 'An email address cannot contain spaces.';

  const labels = domain.split('.');
  if (labels.length < 2) {
    // The overwhelmingly common form of this mistake, and the one that caused the incident.
    return `"${domain}" is missing its ending — did you mean ${domain}.com?`;
  }
  if (labels.some((l) => l === '')) {
    return `"${domain}" has a misplaced dot.`;
  }

  const tld = labels[labels.length - 1] ?? '';
  // A TLD is always alphabetic. Digits here mean someone typed an IP or fat-fingered the ending.
  if (!/^[a-z]{2,}$/.test(tld) || tld.length > MAX_TLD) {
    return `"${domain}" does not end in a valid domain ending such as .com or .org.`;
  }
  return null;
}
